import { Button } from "@/design-system/Button/Button";
import { CustomizeButton } from "@/design-system/CustomizeButton/CustomizeButton";
import {
  applyBundleChange,
  changeObjectiveBundle,
  fetchLatestObjectiveBundle,
  getObjectivesSelector,
  hasCustomPlanSettings,
} from "@/redux/objectives/objectivesSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { errorMessage, useTranslation } from "@repo/shared/i18n";
import { ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { PlanSettingsPanel } from "./PlanSettingsPanel";

/**
 * The plan settings for the bundle, as on the plan page; Update Plan
 * rebuilds, or only keeps the settings while the bundle is empty.
 */
export const ObjectiveBundleCustomizeButton = () => {
  const { t } = useTranslation("objectives");
  const dispatch = useDispatch<AppDispatch>();
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { bundle, build } = useSelector(getObjectivesSelector);

  return (
    <CustomizeButton
      label={t("plan.customize")}
      compact={isTouch}
      width={420}
      active={hasCustomPlanSettings(bundle)}
      disabled={build.status === "pending"}
      panel={({ compact, close }) => (
        <PlanSettingsPanel
          compact={compact}
          close={close}
          settings={{ excludedModes: bundle.excludedModes }}
          onApply={(settings) => {
            const change = {
              type: "set" as const,
              bundle: { ...bundle, ...settings },
            };
            // Nothing to plan yet: keep the settings for the first build.
            dispatch(
              bundle.groupIds.length === 0
                ? applyBundleChange(change)
                : changeObjectiveBundle(change),
            );
          }}
        />
      )}
    />
  );
};

/**
 * "Plan: {m} Games": opens the newest build's plan. Disabled while the bundle
 * is empty.
 */
export const ObjectiveBundleButton = () => {
  const { t } = useTranslation("objectives");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const { bundle, build } = useSelector(getObjectivesSelector);

  useEffect(() => {
    if (isLoggedIn) dispatch(fetchLatestObjectiveBundle());
  }, [dispatch, isLoggedIn]);

  const isEmpty = bundle.groupIds.length === 0;
  // The planner's verdict: a picked objective needs a game mode Customize excludes.
  const invalid = build.data?.objectives.some(
    (o) => o.status === "mode-excluded",
  );
  const status = isEmpty
    ? t("bar.games", { count: 0 })
    : build.status === "pending"
      ? t("bar.building")
      : build.data
        ? invalid
          ? t("bar.invalid")
          : t("bar.games", { count: build.data.simplePlan.nMatches })
        : build.status === "rejected"
          ? t("bar.noPlan")
          : undefined;
  const label = status ? t("bar.plan", { status }) : t("bar.planEmpty");

  const buildError =
    build.status === "rejected"
      ? errorMessage(build.errorCode, "solver")
      : undefined;

  const disabled = isEmpty || !build.data || build.status === "pending";

  return (
    <Button
      variant="primary"
      size="small"
      className="shrink-0"
      icon={<ArrowRight />}
      iconPosition="right"
      disabled={disabled}
      title={
        buildError ?? (invalid ? t("plan.reason.mode-excluded") : undefined)
      }
      onClick={() => navigate(`/objective-plan/${build.data?.id}`)}
    >
      {label}
    </Button>
  );
};
